/**
 * SuperAdmin Dashboard
 * Platform-wide administration for SuperUsers
 * Features: Tenant management, user impersonation, platform statistics
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2, Users, Server, Activity, Search, Plus, Edit2, Eye,
  MoreVertical, RefreshCw, AlertTriangle, CheckCircle2,
  XCircle, Clock, TrendingUp, Shield, X, LogIn
} from 'lucide-react';
import { useAuth } from '../context/AuthContext.jsx';
import { superadminAPI } from '../../api.js';

// Plan colors for badges
const PLAN_COLORS = {
  trial: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  starter: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  professional: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  enterprise: 'bg-green-500/20 text-green-400 border-green-500/30',
  gift: 'bg-pink-500/20 text-pink-400 border-pink-500/30'
};

// Status colors for badges
const STATUS_COLORS = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  suspended: 'bg-red-500/20 text-red-400 border-red-500/30',
  cancelled: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  trial_expired: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
};

// Status icons
const STATUS_ICONS = {
  active: CheckCircle2,
  suspended: XCircle,
  cancelled: XCircle,
  trial_expired: Clock
};

/**
 * Stat Card Component
 */
function StatCard({ icon: Icon, label, value, trend, color = 'blue' }) {
  const colorClasses = {
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/20',
    green: 'from-green-500/20 to-green-600/10 border-green-500/20',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/20',
    orange: 'from-orange-500/20 to-orange-600/10 border-orange-500/20'
  };

  const iconColors = {
    blue: 'text-blue-400',
    green: 'text-green-400',
    purple: 'text-purple-400',
    orange: 'text-orange-400'
  };

  return (
    <div className={`p-4 rounded-xl bg-gradient-to-br ${colorClasses[color]} border`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-400">{label}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {trend && (
            <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
              <TrendingUp size={12} /> {trend}
            </p>
          )}
        </div>
        <div className={`p-2 rounded-lg bg-slate-800/50 ${iconColors[color]}`}>
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
}

/**
 * Tenant Row Component
 */
function TenantRow({ tenant, onView, onEdit, onImpersonate }) {
  const [showMenu, setShowMenu] = useState(false);
  const StatusIcon = STATUS_ICONS[tenant.status] || CheckCircle2;

  return (
    <tr className="border-b border-slate-800 hover:bg-slate-800/30 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
            style={{ backgroundColor: tenant.primary_color || '#3b82f6' }}
          >
            {tenant.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="text-white font-medium">{tenant.name}</div>
            <div className="text-xs text-slate-500">{tenant.subdomain}.fleetmanager.com</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-1 rounded-full text-xs border ${PLAN_COLORS[tenant.plan] || PLAN_COLORS.trial}`}>
          {tenant.plan}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <StatusIcon size={14} className={STATUS_COLORS[tenant.status]?.split(' ')[1] || 'text-slate-400'} />
          <span className={`px-2 py-1 rounded-full text-xs border ${STATUS_COLORS[tenant.status] || STATUS_COLORS.active}`}>
            {tenant.status}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 text-slate-400 text-sm">
        {tenant.user_count || 0} / {tenant.max_users}
      </td>
      <td className="px-4 py-3 text-slate-400 text-sm">
        {tenant.vehicle_count || 0} / {tenant.max_vehicles}
      </td>
      <td className="px-4 py-3">
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          >
            <MoreVertical size={16} />
          </button>
          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 mt-1 w-48 bg-slate-800 rounded-lg shadow-xl border border-slate-700 py-1 z-20">
                <button
                  onClick={() => { onView(tenant); setShowMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                >
                  <Eye size={14} /> View Details
                </button>
                <button
                  onClick={() => { onEdit(tenant); setShowMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                >
                  <Edit2 size={14} /> Edit Tenant
                </button>
                <button
                  onClick={() => { onImpersonate(tenant); setShowMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-blue-400 hover:bg-slate-700 flex items-center gap-2"
                >
                  <LogIn size={14} /> Impersonate Admin
                </button>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

/**
 * Tenant Detail Modal
 */
function TenantDetailModal({ tenant, onClose, onImpersonate }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await superadminAPI.getTenantUsers(tenant.id);
        if (response.ok) {
          setUsers(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch tenant users:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [tenant.id]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl border border-slate-800 w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold"
              style={{ backgroundColor: tenant.primary_color || '#3b82f6' }}
            >
              {tenant.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{tenant.name}</h2>
              <p className="text-sm text-slate-500">{tenant.subdomain}.fleetmanager.com</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Tenant Info */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Plan</p>
              <span className={`px-2 py-1 rounded-full text-xs border ${PLAN_COLORS[tenant.plan]}`}>
                {tenant.plan}
              </span>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Status</p>
              <span className={`px-2 py-1 rounded-full text-xs border ${STATUS_COLORS[tenant.status]}`}>
                {tenant.status}
              </span>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Utilisateurs</p>
              <p className="text-white font-medium">{tenant.user_count || 0} / {tenant.max_users}</p>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Véhicules</p>
              <p className="text-white font-medium">{tenant.vehicle_count || 0} / {tenant.max_vehicles}</p>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg col-span-2">
              <p className="text-xs text-slate-500 mb-1">Email de facturation</p>
              <p className="text-white">{tenant.billing_email || 'Non défini'}</p>
            </div>
          </div>

          {/* Users List */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Users size={16} /> Utilisateurs du tenant
            </h3>
            {loading ? (
              <div className="text-center py-8 text-slate-400">
                <RefreshCw className="animate-spin mx-auto mb-2" size={20} />
                Chargement des utilisateurs...
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                Aucun utilisateur trouvé
              </div>
            ) : (
              <div className="space-y-2">
                {users.map(user => (
                  <div
                    key={user.id}
                    className="p-3 bg-slate-800/50 rounded-lg flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-white text-sm">
                        {user.first_name?.charAt(0) || user.email?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="text-white text-sm">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 rounded text-xs bg-slate-700 text-slate-300">
                        {user.role}
                      </span>
                      <button
                        onClick={() => onImpersonate(user)}
                        className="p-1.5 rounded-lg hover:bg-slate-700 text-blue-400 hover:text-blue-300"
                        title="Se connecter en tant que cet utilisateur"
                      >
                        <LogIn size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Create/Edit Tenant Modal
 */
function TenantFormModal({ tenant, onClose, onSave }) {
  const [formData, setFormData] = useState({
    code: tenant?.code || '',
    name: tenant?.name || '',
    subdomain: tenant?.subdomain || '',
    plan: tenant?.plan || 'trial',
    status: tenant?.status || 'active',
    max_users: tenant?.max_users || 5,
    max_vehicles: tenant?.max_vehicles || 10,
    max_organizations: tenant?.max_organizations || 1,
    billing_email: tenant?.billing_email || '',
    primary_color: tenant?.primary_color || '#3b82f6'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isEditing = !!tenant?.id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      let response;
      if (isEditing) {
        response = await superadminAPI.updateTenant(tenant.id, formData);
      } else {
        response = await superadminAPI.createTenant(formData);
      }

      if (response.ok) {
        onSave(response.data);
      } else {
        throw new Error(response.error || 'Failed to save tenant');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl border border-slate-800 w-full max-w-lg">
        {/* Header */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">
            {isEditing ? 'Edit Tenant' : 'Create New Tenant'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Code</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                placeholder="acme"
                required
                disabled={isEditing}
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Subdomain</label>
              <input
                type="text"
                value={formData.subdomain}
                onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                placeholder="acme"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Raison sociale</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              placeholder="ACME Corporation"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Plan</label>
              <select
                value={formData.plan}
                onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              >
                <option value="trial">Trial</option>
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
                <option value="gift">Gift (Beta/VIP)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Statut</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              >
                <option value="active">Actif</option>
                <option value="suspended">Suspendu</option>
                <option value="cancelled">Annulé</option>
                <option value="trial_expired">Essai expiré</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Utilisateurs max</label>
              <input
                type="number"
                value={formData.max_users}
                onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                min="1"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Véhicules max</label>
              <input
                type="number"
                value={formData.max_vehicles}
                onChange={(e) => setFormData({ ...formData, max_vehicles: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                min="1"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Organisations max</label>
              <input
                type="number"
                value={formData.max_organizations}
                onChange={(e) => setFormData({ ...formData, max_organizations: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                min="1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Email de facturation</label>
            <input
              type="email"
              value={formData.billing_email}
              onChange={(e) => setFormData({ ...formData, billing_email: e.target.value })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              placeholder="billing@acme.com"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Couleur de marque</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={formData.primary_color}
                onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                className="w-10 h-10 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.primary_color}
                onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <RefreshCw className="animate-spin" size={16} />}
              {isEditing ? 'Update' : 'Create'} Tenant
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Main SuperAdmin Dashboard Component
 */
export function SuperAdminDashboard() {
  const { impersonate, isImpersonating, stopImpersonation } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Modal states
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tenantsRes, statsRes] = await Promise.all([
        superadminAPI.getTenants(),
        superadminAPI.getStats()
      ]);

      if (tenantsRes.ok) {
        setTenants(tenantsRes.data);
      }
      if (statsRes.ok) {
        setStats(statsRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch superadmin data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter tenants
  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tenant.subdomain.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlan = filterPlan === 'all' || tenant.plan === filterPlan;
    const matchesStatus = filterStatus === 'all' || tenant.status === filterStatus;
    return matchesSearch && matchesPlan && matchesStatus;
  });

  // Handlers
  const handleViewTenant = (tenant) => {
    setSelectedTenant(tenant);
    setShowDetailModal(true);
  };

  const handleEditTenant = (tenant) => {
    setEditingTenant(tenant);
    setShowFormModal(true);
  };

  const handleCreateTenant = () => {
    setEditingTenant(null);
    setShowFormModal(true);
  };

  const handleSaveTenant = (savedTenant) => {
    setShowFormModal(false);
    fetchData();
  };

  const handleImpersonate = async (userOrTenant) => {
    // If it's a tenant, find the admin user
    if (userOrTenant.subdomain) {
      // It's a tenant, need to fetch admin user
      try {
        const response = await superadminAPI.getTenantUsers(userOrTenant.id);
        if (response.ok && response.data.length > 0) {
          const adminUser = response.data.find(u => u.role === 'admin') || response.data[0];
          await impersonate(adminUser.id);
          window.location.reload();
        }
      } catch (err) {
        console.error('Failed to impersonate:', err);
      }
    } else {
      // It's a user
      try {
        await impersonate(userOrTenant.id);
        window.location.reload();
      } catch (err) {
        console.error('Failed to impersonate:', err);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <RefreshCw className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-6">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="mb-4 p-3 bg-orange-500/20 border border-orange-500/30 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-orange-400">
            <AlertTriangle size={18} />
            <span>You are currently impersonating a user</span>
          </div>
          <button
            onClick={stopImpersonation}
            className="px-3 py-1 rounded bg-orange-500 text-white text-sm hover:bg-orange-400"
          >
            Stop Impersonation
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Shield className="text-blue-500" />
            SuperAdmin Dashboard
          </h1>
          <p className="text-slate-400 mt-1">Platform-wide administration</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
          >
            <RefreshCw size={18} />
          </button>
          <button
            onClick={handleCreateTenant}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 flex items-center gap-2"
          >
            <Plus size={18} /> New Tenant
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={Building2}
          label="Total Tenants"
          value={stats?.total_tenants || tenants.length}
          color="blue"
        />
        <StatCard
          icon={Users}
          label="Total Users"
          value={stats?.total_users || 0}
          color="green"
        />
        <StatCard
          icon={Activity}
          label="Active Tenants"
          value={stats?.active_tenants || tenants.filter(t => t.status === 'active').length}
          color="purple"
        />
        <StatCard
          icon={Server}
          label="Active Users (7d)"
          value={stats?.active_users_7d || 0}
          color="orange"
        />
      </div>

      {/* Tenants Section */}
      <div className="bg-slate-900 rounded-xl border border-slate-800">
        {/* Filters */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <input
                type="text"
                placeholder="Rechercher un tenant..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <select
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
              className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
            >
              <option value="all">Tous les plans</option>
              <option value="trial">Trial</option>
              <option value="starter">Starter</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
              <option value="gift">Gift</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="suspended">Suspendu</option>
              <option value="cancelled">Annulé</option>
              <option value="trial_expired">Essai expiré</option>
            </select>
          </div>
        </div>

        {/* Tenants Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Tenant</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Users</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vehicles</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTenants.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-slate-500">
                    {searchTerm || filterPlan !== 'all' || filterStatus !== 'all'
                      ? 'Aucun tenant ne correspond aux filtres'
                      : 'Aucun tenant trouvé'}
                  </td>
                </tr>
              ) : (
                filteredTenants.map(tenant => (
                  <TenantRow
                    key={tenant.id}
                    tenant={tenant}
                    onView={handleViewTenant}
                    onEdit={handleEditTenant}
                    onImpersonate={handleImpersonate}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showDetailModal && selectedTenant && (
        <TenantDetailModal
          tenant={selectedTenant}
          onClose={() => { setShowDetailModal(false); setSelectedTenant(null); }}
          onImpersonate={handleImpersonate}
        />
      )}

      {showFormModal && (
        <TenantFormModal
          tenant={editingTenant}
          onClose={() => { setShowFormModal(false); setEditingTenant(null); }}
          onSave={handleSaveTenant}
        />
      )}
    </div>
  );
}

export default SuperAdminDashboard;
